import { useState, useEffect, useRef } from 'react';
import { searchUsers } from '../../api/users';
import { addClubMember } from '../../api/clubs';
import type { ClubMemberResponse, ClubRole, UserSearchResult } from '../../types/api';

interface Props {
  clubId: number;
  open: boolean;
  onClose: () => void;
  onAdded: (member: ClubMemberResponse) => void;
}

export function AddMemberDialog({ clubId, open, onClose, onAdded }: Props) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserSearchResult[]>([]);
  const [selected, setSelected] = useState<UserSearchResult | null>(null);
  const [role, setRole] = useState<ClubRole>('Member');
  const [busy, setBusy] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
      setSelected(null);
      setRole('Member');
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await searchUsers(query.trim());
        setResults(res);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  if (!open) return null;

  async function handleAdd() {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      const member = await addClubMember(clubId, selected.id, role);
      onAdded(member);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to add member');
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-xl bg-white dark:bg-slate-800 p-6 shadow-xl">
        <h2 className="mb-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
          Add member
        </h2>
        <p className="mb-4 text-sm text-slate-600 dark:text-slate-300">
          Search by name or email. To make someone Owner, add them first then use Transfer Ownership.
        </p>

        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setSelected(null);
          }}
          placeholder="Search users…"
          disabled={busy}
          className="mb-2 w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:opacity-50"
        />

        {searching && (
          <p className="mb-2 text-xs text-slate-400">Searching…</p>
        )}

        {!searching && results.length > 0 && !selected && (
          <ul className="mb-3 max-h-48 overflow-y-auto rounded-md border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700">
            {results.map((u) => (
              <li key={u.id}>
                <button
                  type="button"
                  onClick={() => {
                    setSelected(u);
                    setQuery(u.displayName);
                  }}
                  className="w-full px-3 py-2 text-left text-sm text-slate-900 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                >
                  <span className="font-medium">{u.displayName}</span>
                  {u.userName !== u.displayName && (
                    <span className="ml-2 text-slate-400 text-xs">{u.userName}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}

        {selected && (
          <p className="mb-3 rounded-md bg-teal-50 dark:bg-teal-900/20 px-3 py-2 text-sm text-teal-700 dark:text-teal-400">
            Adding: <strong>{selected.displayName}</strong>
          </p>
        )}

        <select
          value={role}
          onChange={(e) => setRole(e.target.value as ClubRole)}
          disabled={busy}
          className="mb-4 w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 disabled:opacity-50"
        >
          <option value="Member">Member</option>
          <option value="Admin">Admin</option>
          <option value="Guest">Guest</option>
        </select>

        {error && <p className="mb-2 text-sm text-red-500">{error}</p>}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-slate-300 dark:border-slate-600 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAdd}
            disabled={busy || !selected}
            className="rounded-md bg-teal-600 hover:bg-teal-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {busy ? 'Adding…' : 'Add member'}
          </button>
        </div>
      </div>
    </div>
  );
}
